import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useEffect, useState, useRef } from "react";
import AkkhorLayout from "../layout/akkhor";
import { useSelector } from "react-redux";
import AlertErrorForm from "../component/AlertErrorForm";
import Spinner from "../component/Spinner";
import Axios from "axios";
import { URL_LIST_ALL } from "../configs/api";
import { Link, useParams } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { Modal } from "react-bootstrap";
import QRCode from "qrcode.react";
import SearchBoxModal from "../component/SearchBoxModal";
import { isSeatRealtimeEvent } from "./realtime";
import { jwtDecode } from "jwt-decode";

// Domain FE prod — QR chứa URL này để quét bằng Zalo/camera vẫn check-in được (khi đã login)
const FE_BASE = "https://noibo.bhsgroup.vn";
const checkinUrl = (token) => `${FE_BASE}/checkin-cho-ngoi?token=${token}`;

const SEAT_STATUS_OPTIONS = [
    { value: 1, label: 'Còn trống', badge: 'bg-success' },
    { value: 2, label: 'Đã có người đặt', badge: 'bg-warning text-dark' },
    { value: 0, label: 'Ẩn', badge: 'bg-secondary' },
];

const getSeatStatus = (status) => SEAT_STATUS_OPTIONS.find(item => item.value === Number(status)) || SEAT_STATUS_OPTIONS[0];

const getSeatSortValue = (seat) => {
    const seatIndex = Number(seat.seat_index);
    if (!Number.isNaN(seatIndex)) return seatIndex;
    const codeNumber = String(seat.code || '').match(/\d+$/);
    if (codeNumber) return Number(codeNumber[0]);
    return Number(seat.id) || 0;
};

const sortSeatsLowToHigh = (list) => list.slice().sort((a, b) => getSeatSortValue(a) - getSeatSortValue(b));

const toDatetimeLocal = (timestamp) => {
    const value = Number(timestamp);
    if (!value) return '';
    const date = new Date(value * 1000);
    const pad = (num) => String(num).padStart(2, '0');
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + 'T' + pad(date.getHours()) + ':' + pad(date.getMinutes());
};

const defaultManualBusyUntil = () => {
    const date = new Date(Date.now() + 60 * 60 * 1000);
    const pad = (num) => String(num).padStart(2, '0');
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + 'T' + pad(date.getHours()) + ':' + pad(date.getMinutes());
};

const datetimeLocalToUnix = (value) => {
    if (!value) return null;
    const time = new Date(value).getTime();
    if (Number.isNaN(time)) return null;
    return Math.floor(time / 1000);
};

const formatSeatOpenTime = (timestamp) => {
    const value = Number(timestamp);
    if (!value) return '';
    const date = new Date(value * 1000);
    const pad = (num) => String(num).padStart(2, '0');
    return pad(date.getHours()) + ':' + pad(date.getMinutes()) + ' ' + pad(date.getDate()) + '/' + pad(date.getMonth() + 1);
};

const formatSeatDateTime = (timestamp) => {
    const value = Number(timestamp);
    if (!value) return '';
    const date = new Date(value * 1000);
    const pad = (num) => String(num).padStart(2, '0');
    return pad(date.getHours()) + ':' + pad(date.getMinutes()) + ' ' + pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + '/' + date.getFullYear();
};

const getCheckinDeadline = (booking) => Number(booking?.effective_checkin_deadline || booking?.checkin_deadline || 0);

const bookingStateInfo = (booking) => {
    if (!booking) return null;
    if (booking.booking_state === 'booked') {
        return {
            cls: 'text-warning',
            text: 'Chưa quét QR',
            note: 'Nếu chưa xác nhận QR trước ' + formatSeatDateTime(getCheckinDeadline(booking)) + ', hệ thống sẽ tự hủy lượt đặt và mở lại chỗ.',
        };
    }
    if (booking.booking_state === 'active') {
        return {
            cls: 'text-success',
            text: 'Đã quét QR',
            note: 'Chỗ đang được sử dụng, dự kiến mở lại sau ' + formatSeatDateTime(booking.dateend) + '.',
        };
    }
    return {
        cls: 'text-muted',
        text: booking.booking_state,
        note: '',
    };
};

// Thêm/sửa địa điểm + quản lý chỗ ngồi (tạo hàng loạt theo số chỗ) + in QR
export default function FormDiaDiem() {
    const { user } = useSelector((state) => state.auth);
    const { saveRealTimeData, datasocket } = useSelector((state) => state.save);
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [refresh, setRefresh] = useState(false);

    const [seats, setSeats] = useState([]);
    const [seatCount, setSeatCount] = useState('');
    const [syncing, setSyncing] = useState(false);
    const [statusSavingId, setStatusSavingId] = useState(null);
    const [statusDraft, setStatusDraft] = useState(1);
    const [manualBusyUntil, setManualBusyUntil] = useState('');
    const [activeSeatBookings, setActiveSeatBookings] = useState([]);

    const [showQR, setShowQR] = useState(false);
    const [qrSeat, setQrSeat] = useState(null);
    const [seatBookings, setSeatBookings] = useState([]);
    const [seatBookingsLoading, setSeatBookingsLoading] = useState(false);
    const qrRef = useRef(null);
    const seatSocketRef = useRef(null);

    // Chọn nhiều chỗ để đổi trạng thái hàng loạt
    const [selectMode, setSelectMode] = useState(false);
    const [selectedSeatIds, setSelectedSeatIds] = useState([]);
    const [bulkStatus, setBulkStatus] = useState(1);
    const [bulkManualBusyUntil, setBulkManualBusyUntil] = useState('');
    const [bulkSaving, setBulkSaving] = useState(false);

    const defaultdetail = {
        title: '', code: '', address: '', aprover_id: '', activation_minutes: 15,
        open_time: '', close_time: '', status: 1,
    };

    const fetchActiveSeatBookings = () => {
        if (!(id > 0)) return;
        Axios.get(URL_LIST_ALL + "seat_bookings?limit=1000&location_id=" + id + "&status=1&availability=1", user.config).then((res) => {
            if (res.data.status === "success") setActiveSeatBookings(res.data.data || []);
        }).catch((err) => console.log(err));
    };

    // Nạp địa điểm + chỗ ngồi khi edit
    useEffect(function () {
        if (id > 0) {
            let mounted = true;
            Axios.get(URL_LIST_ALL + "seat_locations/" + id, user.config).then(async (res) => {
                if (res.data.status === "success" && mounted) setDetail(res.data.data);
            }).catch((err) => console.log(err));
            Axios.get(URL_LIST_ALL + "seat_seats?limit=1000&location_id=" + id, user.config).then((res) => {
                if (res.data.status === "success" && mounted) {
                    const list = sortSeatsLowToHigh(res.data.data);
                    setSeats(list);
                    setSeatCount(String(list.length));
                    setQrSeat(prev => {
                        if (!prev) return prev;
                        return list.find(seat => seat.id === prev.id) || prev;
                    });
                    const validIds = new Set(list.map(seat => seat.id));
                    setSelectedSeatIds(prev => prev.filter(seatId => validIds.has(seatId)));
                }
            }).catch((err) => console.log(err));
            fetchActiveSeatBookings();
            return () => (mounted = false);
        }
    }, [user, id, refresh]);

    useEffect(function () {
        if (!(id > 0)) return;
        const intervalId = setInterval(fetchActiveSeatBookings, 5000);
        return () => clearInterval(intervalId);
    }, [user, id]);

    useEffect(function () {
        if (id > 0 && (isSeatRealtimeEvent(saveRealTimeData) || isSeatRealtimeEvent(datasocket))) {
            setRefresh(prev => !prev);
        }
    }, [saveRealTimeData, datasocket, id]);

    useEffect(function () {
        if (showQR && qrSeat) {
            setStatusDraft(Number(qrSeat.status ?? 1));
            setManualBusyUntil(toDatetimeLocal(qrSeat.manual_busy_until));
        }
    }, [showQR, qrSeat]);

    useEffect(function () {
        if (!(id > 0)) return;
        let closed = false;
        const connectSeatSocket = () => {
            const ws = new WebSocket('wss://socket.bhsgroup.vn');
            seatSocketRef.current = ws;
            ws.onmessage = (evt) => {
                try {
                    const data = jwtDecode(evt.data);
                    if (data && data.channel === 'bhs_socket' && isSeatRealtimeEvent(data)) {
                        setRefresh(prev => !prev);
                    }
                } catch (e) { /* ignore malformed socket payload */ }
            };
            ws.onclose = () => {
                if (!closed) setTimeout(connectSeatSocket, 1000);
            };
        };
        connectSeatSocket();
        return () => {
            closed = true;
            if (seatSocketRef.current) seatSocketRef.current.close();
        };
    }, [id]);

    const formik = useFormik({
        initialValues: detail ? detail : defaultdetail,
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            title: Yup.string().trim().nullable().required('Nhập tên địa điểm').max(150, "Tối đa 150 ký tự"),
            code: Yup.string().trim().nullable().required('Nhập mã địa điểm (dùng làm tiền tố mã chỗ)').max(20, "Tối đa 20 ký tự"),
            activation_minutes: Yup.number().typeError('Nhập số phút').min(1, 'Tối thiểu 1 phút').required('Nhập thời gian kích hoạt'),
        }),
        onSubmit: (values, { setSubmitting }) => {
            setLoading(true);
            const done = (res, msg, newId) => {
                setLoading(false); setSubmitting(false);
                if (res.data.status === "success") {
                    confirmAlert({ title: 'Thành công', message: msg, buttons: [{ label: 'OK' }] });
                    if (newId) window.location.href = '/edit-dia-diem-cho-ngoi/' + newId;
                    else setRefresh(r => !r);
                } else {
                    confirmAlert({ title: 'Lỗi', message: res.data.message, buttons: [{ label: 'OK' }] });
                }
            };
            if (id > 0) {
                Axios.put(URL_LIST_ALL + 'seat_locations/' + id, values, user.config)
                    .then(res => done(res, 'Cập nhật địa điểm thành công')).catch(err => console.log(err));
            } else {
                Axios.post(URL_LIST_ALL + 'seat_locations', values, user.config)
                    .then(res => done(res, 'Đã tạo địa điểm. Tiếp tục thêm chỗ ngồi bên dưới.', res.data.id))
                    .catch(err => console.log(err));
            }
        },
    });

    // Tạo/đặt lại số chỗ
    const syncSeats = () => {
        const count = parseInt(seatCount, 10);
        if (isNaN(count) || count < 0) {
            confirmAlert({ title: 'Lỗi', message: 'Nhập số chỗ hợp lệ', buttons: [{ label: 'OK' }] });
            return;
        }
        confirmAlert({
            title: 'Xác nhận', message: `Đặt số chỗ của địa điểm này thành ${count}?`,
            buttons: [
                { label: 'Đồng ý', onClick: () => {
                    setSyncing(true);
                    Axios.post(URL_LIST_ALL + 'seat_seats_sync', { location_id: id, count }, user.config).then(res => {
                        setSyncing(false);
                        if (res.data.status === "success") { setRefresh(r => !r); }
                        else confirmAlert({ title: 'Không thực hiện được', message: res.data.message, buttons: [{ label: 'OK' }] });
                    }).catch(() => { setSyncing(false); });
                }},
                { label: 'Huỷ' },
            ],
        });
    };

    const openQR = (seat) => {
        setQrSeat(seat);
        setStatusDraft(Number(seat.status ?? 1));
        setManualBusyUntil(toDatetimeLocal(seat.manual_busy_until));
        setShowQR(true);
    };

    useEffect(function () {
        if (!showQR || !qrSeat) {
            setSeatBookings([]);
            return;
        }
        setSeatBookingsLoading(true);
        Axios.get(URL_LIST_ALL + "seat_bookings?limit=20&seat_id=" + qrSeat.id + "&status=1", user.config).then(res => {
            setSeatBookingsLoading(false);
            if (res.data.status === "success") setSeatBookings(res.data.data || []);
        }).catch(err => {
            setSeatBookingsLoading(false);
            console.log(err);
        });
    }, [showQR, qrSeat, user, refresh]);

    const handleStatusDraftChange = (statusValue) => {
        const status = parseInt(statusValue, 10);
        setStatusDraft(status);
        if (status === 2 && !manualBusyUntil) setManualBusyUntil(defaultManualBusyUntil());
    };

    // Chọn nhiều chỗ để đổi trạng thái hàng loạt
    const toggleSeatSelected = (seatId) => {
        setSelectedSeatIds(prev => prev.includes(seatId) ? prev.filter(id => id !== seatId) : prev.concat([seatId]));
    };

    const isAllSeatsSelected = seats.length > 0 && selectedSeatIds.length === seats.length;
    const toggleSelectAllSeats = () => {
        setSelectedSeatIds(isAllSeatsSelected ? [] : seats.map(seat => seat.id));
    };

    // Bật/tắt chế độ chọn nhiều: khi tắt, xóa hết lựa chọn đang chọn.
    const toggleSelectMode = () => {
        setSelectMode(prev => {
            if (prev) setSelectedSeatIds([]);
            return !prev;
        });
    };

    const handleBulkStatusChange = (statusValue) => {
        const status = parseInt(statusValue, 10);
        setBulkStatus(status);
        if (status === 2 && !bulkManualBusyUntil) setBulkManualBusyUntil(defaultManualBusyUntil());
    };

    const applyBulkStatus = () => {
        if (selectedSeatIds.length === 0) return;
        const status = parseInt(bulkStatus, 10);
        const manualBusyUntilUnix = status === 2 ? datetimeLocalToUnix(bulkManualBusyUntil) : null;

        if (status === 2 && (!manualBusyUntilUnix || manualBusyUntilUnix <= Math.floor(Date.now() / 1000))) {
            confirmAlert({ title: 'Thiếu thời gian mở', message: 'Chọn thời gian mở lại lớn hơn thời điểm hiện tại.', buttons: [{ label: 'OK' }] });
            return;
        }

        const statusLabel = getSeatStatus(status).label;
        confirmAlert({
            title: 'Xác nhận',
            message: `Đổi trạng thái ${selectedSeatIds.length} chỗ đã chọn thành "${statusLabel}"?`,
            buttons: [
                { label: 'Đồng ý', onClick: () => {
                    setBulkSaving(true);
                    const values = {
                        status,
                        manual_busy_until: manualBusyUntilUnix,
                        manual_busy_until_picker: manualBusyUntilUnix ? new Date(manualBusyUntilUnix * 1000).toISOString().slice(0, 19).replace('T', ' ') : null,
                    };
                    Promise.all(selectedSeatIds.map(seatId =>
                        Axios.put(URL_LIST_ALL + 'seat_seats/' + seatId, values, user.config).then(res => ({ seatId, res }))
                    )).then(results => {
                        setBulkSaving(false);
                        const failed = results.filter(item => item.res.data.status !== "success");
                        setSelectedSeatIds([]);
                        setRefresh(r => !r);
                        if (failed.length === 0) {
                            confirmAlert({ title: 'Thành công', message: `Đã cập nhật ${results.length} chỗ ngồi.`, buttons: [{ label: 'OK' }] });
                        } else {
                            confirmAlert({ title: 'Có lỗi xảy ra', message: `${failed.length}/${results.length} chỗ cập nhật thất bại: ${failed[0].res.data.message}`, buttons: [{ label: 'OK' }] });
                        }
                    }).catch(() => {
                        setBulkSaving(false);
                        confirmAlert({ title: 'Lỗi', message: 'Không cập nhật được trạng thái các chỗ đã chọn', buttons: [{ label: 'OK' }] });
                    });
                }},
                { label: 'Huỷ' },
            ],
        });
    };

    const saveSeatStatus = (seat) => {
        const status = parseInt(statusDraft, 10);
        const oldStatus = Number(seat.status ?? 1);
        const oldManualBusyUntil = seat.manual_busy_until || null;
        const manualBusyUntilUnix = status === 2 ? datetimeLocalToUnix(manualBusyUntil) : null;

        if (status === 2 && (!manualBusyUntilUnix || manualBusyUntilUnix <= Math.floor(Date.now() / 1000))) {
            confirmAlert({ title: 'Thiếu thời gian mở', message: 'Chọn thời gian mở lại lớn hơn thời điểm hiện tại.', buttons: [{ label: 'OK' }] });
            return;
        }

        setStatusSavingId(seat.id);

        const values = {
            status,
            manual_busy_until: manualBusyUntilUnix,
            manual_busy_until_picker: manualBusyUntilUnix ? new Date(manualBusyUntilUnix * 1000).toISOString().slice(0, 19).replace('T', ' ') : null,
        };

        Axios.put(URL_LIST_ALL + 'seat_seats/' + seat.id, values, user.config).then(res => {
            setStatusSavingId(null);
            if (res.data.status === "success") {
                const nextSeat = { ...seat, status, manual_busy_until: manualBusyUntilUnix };
                setSeats(prev => prev.map(item => item.id === seat.id ? { ...item, status, manual_busy_until: manualBusyUntilUnix } : item));
                setQrSeat(nextSeat);
                setRefresh(r => !r);
            } else {
                setStatusDraft(oldStatus);
                setManualBusyUntil(toDatetimeLocal(oldManualBusyUntil));
                confirmAlert({ title: 'Không cập nhật được', message: res.data.message, buttons: [{ label: 'OK' }] });
            }
        }).catch(() => {
            setStatusSavingId(null);
            setStatusDraft(oldStatus);
            setManualBusyUntil(toDatetimeLocal(oldManualBusyUntil));
            confirmAlert({ title: 'Lỗi', message: 'Không cập nhật được trạng thái chỗ', buttons: [{ label: 'OK' }] });
        });
    };

    const downloadQR = () => {
        const canvas = qrRef.current?.querySelector('canvas');
        if (canvas) {
            const pngUrl = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
            const a = document.createElement("a");
            a.href = pngUrl;
            a.download = "qr-" + (qrSeat.code || qrSeat.id) + ".png";
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
        }
    };

    const bookingBlocksSeat = (booking) => {
        const now = Math.floor(Date.now() / 1000);
        if (!booking || Number(booking.status) !== 1) return false;
        const checkinDeadline = getCheckinDeadline(booking);
        if (booking.booking_state === 'booked' && checkinDeadline > 0 && checkinDeadline < now) return false;
        if (booking.booking_state === 'active' && Number(booking.dateend) > 0 && Number(booking.dateend) < now) return false;
        return true;
    };

    const activeBookingForSeat = (seatId) => activeSeatBookings.find(booking => booking.seat_id == seatId && bookingBlocksSeat(booking));

    const getSeatDisplayStatus = (seat) => {
        if (Number(seat.status) === 0) return getSeatStatus(seat.status);
        const booking = activeBookingForSeat(seat.id);
        if (booking) return { value: 2, label: 'Đã có người đặt', badge: 'bg-warning text-dark', booking };
        return getSeatStatus(seat.status);
    };

    // In mã tổng: mở cửa sổ in với lưới toàn bộ QR (mỗi QR có số thứ tự góc)
    const printAll = () => {
        if (seats.length === 0) return;
        const win = window.open('', '_blank');
        const loc = detail || formik.values;
        const cells = seats.map(s => {
            const idx = s.seat_index || '';
            // Dùng API qrserver để render QR trong trang in (ảnh tĩnh, không cần JS)
            const url = checkinUrl(s.qr_token);
            const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(url)}`;
            return `<div class="cell">
                        <div class="idx">${idx}</div>
                        <img src="${qrImg}" alt="${s.code}" />
                        <div class="code">${s.code || ''}</div>
                    </div>`;
        }).join('');
        win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>In mã QR - ${loc.title || ''}</title>
            <style>
              body{font-family:Arial,sans-serif;margin:16px}
              h2{text-align:center}
              .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}
              .cell{border:1px solid #ccc;border-radius:8px;padding:8px;text-align:center;position:relative;page-break-inside:avoid}
              .idx{position:absolute;top:6px;left:8px;font-weight:bold;font-size:18px;background:#0d6efd;color:#fff;border-radius:50%;width:28px;height:28px;line-height:28px}
              .code{margin-top:6px;font-weight:bold}
              img{width:100%;max-width:220px;height:auto}
              @media print{.noprint{display:none}}
            </style></head><body>
            <h2>${loc.title || ''} ${loc.code ? '(' + loc.code + ')' : ''}</h2>
            <button class="noprint" onclick="window.print()" style="display:block;margin:0 auto 12px;padding:8px 16px">In ngay</button>
            <div class="grid">${cells}</div>
            </body></html>`);
        win.document.close();
    };

    const qrValue = qrSeat ? checkinUrl(qrSeat.qr_token) : '';

    return (
        <AkkhorLayout idrole="quan-tri-cho-ngoi">
            <div className="breadcrumbs-area">
                <ul>
                    <li><Link to="/">Trang chủ</Link></li>
                    <li><Link to="/dia-diem-cho-ngoi">Quản lý địa điểm</Link></li>
                    <li>{id ? 'Sửa địa điểm' : 'Thêm địa điểm'}</li>
                </ul>
            </div>

            <div className="card height-auto mt-4">
                <div className="card-body">
                    <div className="heading-layout1">
                        <div className="item-title"><h3>{id ? 'Thông tin địa điểm' : 'Thêm địa điểm'}</h3></div>
                    </div>
                    <form className="new-added-form" method="post" onSubmit={formik.handleSubmit}>
                        <div className="row">
                            <div className="col-12 col-sm-3 form-group">
                                <label>Tên địa điểm <span className="text-danger">*</span></label>
                                <input type="text" placeholder="VD: Tầng 5 - Số 6 Lê Văn Thiêm" className="form-control height32" {...formik.getFieldProps("title")} autoComplete="off" />
                                <AlertErrorForm formik={formik} name="title"></AlertErrorForm>
                            </div>
                            <div className="col-12 col-sm-3 form-group">
                                <label>Mã địa điểm <span className="text-danger">*</span></label>
                                <input type="text" placeholder="VD: LVT5" className="form-control height32" {...formik.getFieldProps("code")} autoComplete="off" />
                                <AlertErrorForm formik={formik} name="code"></AlertErrorForm>
                                <small className="text-muted">Dùng làm tiền tố mã chỗ: {formik.values.code || 'MÃ'}-01, -02...</small>
                            </div>
                            <div className="col-12 col-sm-3 form-group">
                                <label>Địa chỉ</label>
                                <input type="text" placeholder="Địa chỉ chi tiết" className="form-control height32" {...formik.getFieldProps("address")} autoComplete="off" />
                            </div>
                            <div className="col-12 col-sm-3 form-group">
                                <label>Thời gian kích hoạt (phút) <span className="text-danger">*</span></label>
                                <input type="number" min="1" placeholder="VD: 15" className="form-control height32" {...formik.getFieldProps("activation_minutes")} />
                                <AlertErrorForm formik={formik} name="activation_minutes"></AlertErrorForm>
                            </div>
                            <div className="col-12 col-sm-3 form-group">
                                <label>Giờ mở cửa</label>
                                <input type="time" className="form-control height32" {...formik.getFieldProps("open_time")} />
                            </div>
                            <div className="col-12 col-sm-3 form-group">
                                <label>Giờ đóng cửa</label>
                                <input type="time" className="form-control height32" {...formik.getFieldProps("close_time")} />
                            </div>
                            <div className="col-12 col-sm-3 form-group">
                                <label>Người quản lý:</label>
                                <SearchBoxModal apiname={"users"} placeholder="Chọn người quản trị" colvalue={'id'} colname={'fullname'} defaultValue={formik.values['aprover_id']} name='aprover_id'
                                    onChange={async (e) => { await formik.setFieldValue('aprover_id', e); }} />
                            </div>
                            <div className="col-12 col-sm-3 form-group">
                                <label>Trạng thái</label>
                                <select className="form-select height32" {...formik.getFieldProps("status")}>
                                    <option value="1">Hoạt động</option>
                                    <option value="0">Ngừng hoạt động</option>
                                </select>
                            </div>
                            <div className="col-12 form-group mg-t-8">
                                <button type="submit" disabled={formik.isSubmitting} className="btn-fill-lg btn-gradient-yellow btn-hover-bluedark">{loading ? <Spinner /> : <>Lưu địa điểm</>}</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Quản lý chỗ ngồi — chỉ khi đã có địa điểm (edit) */}
            {id > 0 && (
                <div className="card height-auto mt-4">
                    <div className="card-body">
                        <div className="heading-layout1 mb-3">
                            <div className="item-title"><h3>Chỗ ngồi ({seats.length})</h3></div>
                        </div>

                        <div className="row align-items-end mb-3">
                            <div className="col-8 col-sm-4 form-group mb-0">
                                <label>Số chỗ ngồi</label>
                                <input type="number" min="0" className="form-control height32" value={seatCount} onChange={(e) => setSeatCount(e.target.value)} placeholder="VD: 30" />
                            </div>
                            <div className="col-4 col-sm-3">
                                <button type="button" className="btn btn-success" onClick={syncSeats} disabled={syncing}>{syncing ? 'Đang tạo...' : 'Tạo / Cập nhật chỗ'}</button>
                            </div>
                            <div className="col-12 col-sm-5 text-sm-end mt-2 mt-sm-0">
                                {seats.length > 0 && <button type="button" className="btn btn-primary" onClick={printAll}><i className="fas fa-print"></i> In mã tổng</button>}
                            </div>
                        </div>
                        <small className="text-muted d-block mb-2">Nhập số chỗ rồi bấm "Tạo / Cập nhật chỗ": hệ thống tự sinh mã {formik.values.code || 'MÃ'}-01... Nhấp vào từng chỗ để xem/in mã QR.</small>
                        <small className="text-muted d-block mb-3">Trạng thái "Ẩn" sẽ không hiển thị ở trang Coreworking; "Đã có người đặt" vẫn hiển thị nhưng không cho nhân viên chọn đặt.</small>

                        {seats.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                    {selectMode && (
                                        <div className="form-check d-inline-block">
                                            <input type="checkbox" className="form-check-input" id="select-all-seats" checked={isAllSeatsSelected} onChange={toggleSelectAllSeats} />
                                            <label className="form-check-label" htmlFor="select-all-seats">Chọn tất cả ({selectedSeatIds.length}/{seats.length})</label>
                                        </div>
                                    )}
                                </div>
                                <button type="button" className={"btn btn-sm " + (selectMode ? "btn-outline-danger" : "btn-outline-primary")} onClick={toggleSelectMode}>
                                    {selectMode ? 'Hủy chọn' : (<><i className="fas fa-check-square"></i> Chọn nhiều</>)}
                                </button>
                            </div>
                        )}

                        {selectMode && selectedSeatIds.length > 0 && (
                            <div className="row align-items-end mb-3 p-2" style={{ background: '#f6f9fc', borderRadius: 6 }}>
                                <div className="col-12 col-sm-4 form-group mb-2 mb-sm-0">
                                    <label>Đổi trạng thái {selectedSeatIds.length} chỗ đã chọn thành</label>
                                    <select className="form-select height32" value={bulkStatus} disabled={bulkSaving} onChange={(e) => handleBulkStatusChange(e.target.value)}>
                                        {SEAT_STATUS_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                                    </select>
                                </div>
                                {Number(bulkStatus) === 2 && (
                                    <div className="col-12 col-sm-4 form-group mb-2 mb-sm-0">
                                        <label>Thời gian mở lại</label>
                                        <input type="datetime-local" className="form-control height32" value={bulkManualBusyUntil} disabled={bulkSaving} onChange={(e) => setBulkManualBusyUntil(e.target.value)} />
                                    </div>
                                )}
                                <div className="col-12 col-sm-4">
                                    <button type="button" className="btn btn-success me-2" disabled={bulkSaving} onClick={applyBulkStatus}>{bulkSaving ? 'Đang áp dụng...' : 'Áp dụng'}</button>
                                    <button type="button" className="btn btn-outline-secondary" disabled={bulkSaving} onClick={() => setSelectedSeatIds([])}>Bỏ chọn</button>
                                </div>
                            </div>
                        )}

                        {/* Lưới chỗ: 6 cột desktop, 2 cột mobile */}
                        <div className="row g-2">
                            {seats.length === 0 && <div className="col-12 text-muted">Chưa có chỗ ngồi. Nhập số chỗ ở trên để tạo.</div>}
                            {seats.map(seat => {
                                const statusInfo = getSeatDisplayStatus(seat);
                                const isSelected = selectedSeatIds.includes(seat.id);
                                return (
                                    <div key={seat.id} className="col-6 col-md-2">
                                        <button
                                            type="button"
                                            className={"btn w-100 " + (isSelected ? "btn-primary" : "btn-outline-primary")}
                                            style={{ height: 92, whiteSpace: 'normal', position: 'relative' }}
                                            onClick={() => selectMode ? toggleSeatSelected(seat.id) : openQR(seat)}
                                            title={selectMode ? "Chọn/bỏ chọn chỗ này" : "Xem/in QR và cập nhật trạng thái"}
                                        >
                                            {selectMode && (
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    style={{ position: 'absolute', top: 6, left: 6, margin: 0, pointerEvents: 'none' }}
                                                    checked={isSelected}
                                                    readOnly
                                                    tabIndex={-1}
                                                />
                                            )}
                                            <div style={{ fontWeight: 'bold', fontSize: 18 }}>{seat.seat_index}</div>
                                            <small>{seat.code}</small>
                                            <div><i className="fas fa-qrcode"></i></div>
                                            <span className={"badge " + statusInfo.badge}>{statusInfo.label}</span>
                                            {statusInfo.booking && statusInfo.booking.booking_state === 'booked' && <small className="d-block text-muted">Mở {formatSeatOpenTime(getCheckinDeadline(statusInfo.booking))}</small>}
                                            {statusInfo.booking && statusInfo.booking.booking_state === 'active' && <small className="d-block text-muted">Mở {formatSeatOpenTime(statusInfo.booking.dateend)}</small>}
                                            {Number(seat.status) === 2 && seat.manual_busy_until && <small className="d-block text-muted">Mở {formatSeatOpenTime(seat.manual_busy_until)}</small>}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal QR 1 chỗ */}
            <Modal show={showQR} onHide={() => setShowQR(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Mã QR chỗ: {qrSeat && qrSeat.code}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {qrSeat && (
                        <>
                            <div ref={qrRef} className="d-inline-block position-relative">
                                <QRCode value={qrValue} size={300} />
                                <span style={{ position: 'absolute', top: 6, left: 6, background: '#0d6efd', color: '#fff', borderRadius: '50%', width: 40, height: 40, lineHeight: '40px', fontWeight: 'bold', fontSize: 20 }}>{qrSeat.seat_index}</span>
                            </div>
                            <p className="mt-2 mb-0"><strong>{qrSeat.code}</strong></p>
                            <small className="text-muted">Quét bằng Zalo/camera điện thoại (khi đã đăng nhập) sẽ tự kích hoạt.</small>
                            <div className="text-start mt-3">
                                <label>Trạng thái chỗ</label>
                                <select
                                    className="form-select height32"
                                    value={statusDraft}
                                    disabled={statusSavingId === qrSeat.id}
                                    onChange={(e) => handleStatusDraftChange(e.target.value)}
                                >
                                    {SEAT_STATUS_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                                </select>
                                {Number(statusDraft) === 2 && (
                                    <div className="mt-2">
                                        <label>Thời gian mở lại</label>
                                        <input
                                            type="datetime-local"
                                            className="form-control height32"
                                            value={manualBusyUntil}
                                            disabled={statusSavingId === qrSeat.id}
                                            onChange={(e) => setManualBusyUntil(e.target.value)}
                                        />
                                        <small className="text-muted">Đến thời gian này hệ thống sẽ tự chuyển chỗ về "Còn trống".</small>
                                    </div>
                                )}
                                <small className="text-muted d-block mt-2">Chọn "Ẩn" để không hiển thị chỗ này ở trang Coreworking.</small>
                            </div>
                            <div className="text-start mt-3 p-2" style={{ background: '#f6f9fc', borderRadius: 6 }}>
                                <div className="fw-bold mb-2">Người đang đặt chỗ này</div>
                                {seatBookingsLoading && <div className="text-muted">Đang kiểm tra booking...</div>}
                                {!seatBookingsLoading && seatBookings.length === 0 && <div className="text-muted">Chưa có lượt đặt đang giữ chỗ.</div>}
                                {!seatBookingsLoading && seatBookings.map(booking => {
                                    const stateInfo = bookingStateInfo(booking);
                                    return (
                                        <div key={booking.id} className="mb-2 pb-2 border-bottom">
                                            <div><strong>{booking.user_fullname || booking.user_code || ('User #' + booking.user_id)}</strong></div>
                                            <div>
                                                Thời gian: {formatSeatDateTime(booking.datestart)} - {formatSeatDateTime(booking.dateend)}
                                            </div>
                                            <div>Trạng thái: <span className={stateInfo.cls}>{stateInfo.text}</span></div>
                                            {stateInfo.note && <small className="text-muted">{stateInfo.note}</small>}
                                            {booking.title && <div><small>Ghi chú: {booking.title}</small></div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-success btn-sm" disabled={!qrSeat || statusSavingId === qrSeat.id} onClick={() => saveSeatStatus(qrSeat)}>
                        {statusSavingId === (qrSeat && qrSeat.id) ? 'Đang lưu...' : 'Lưu trạng thái'}
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={downloadQR}>Tải ảnh xuống</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowQR(false)}>Đóng</button>
                </Modal.Footer>
            </Modal>
        </AkkhorLayout>
    );
}
